import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import AdminLayout from '../../components/admin/AdminLayout';
import { useForm } from 'react-hook-form';
import ThemeCustomizer from '../../components/admin/ThemeCustomizer';
import type { AgencyTheme } from '../../types';
import { Upload, Loader, X, Plus } from 'lucide-react';
import { GET_AGENCY } from '../../graphql/admin/queries/agency';
import { UPDATE_AGENCY, UPLOAD_FILE } from '../../graphql/admin/mutations/agency';
import { getImageUrl } from '../../utils/imageUrl';

// Types
interface FormInputs {
  name: string;
  logo?: string;
  description: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  firstTitle: string;
  secondTitle: string;
  imageCover: string;
  socialMedia: {
    facebook: string;
    instagram: string;
    tiktok: string;
  };
  popularDestinations: Array<{
    id: number;
    name: string;
    image: string;
    price: number;
  }>;
  aboutImage: string;
  yearsExperience: number;
  expertise: string;
  satisfiedTravelers: number;
  prestations: string[];
}

// Valeurs par défaut
const defaultTheme: AgencyTheme = {
  primaryColor: '#3B82F6',
  secondaryColor: '#1E40AF',
  textColor: '#111827',
  backgroundColor: '#F3F4F6',
  heroTextColor: '#FFFFFF',
  headerStyle: 'default',
  font: 'Inter',
  accentColor: '#60A5FA'
};

export default function AgencyProfilePage() {
  // Hooks
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormInputs>();
  
  const [theme, setTheme] = useState<AgencyTheme>(defaultTheme);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // États pour stocker les fichiers sélectionnés
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [aboutFile, setAboutFile] = useState<File | null>(null);
  
  // États pour les prévisualisations d'images
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [aboutPreview, setAboutPreview] = useState<string | null>(null);
  
  // Refs pour les inputs de fichiers
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const aboutInputRef = useRef<HTMLInputElement>(null);
  
  // Ref pour les inputs de fichiers des destinations
  const destinationInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  // États pour les prévisualisations d'images des destinations
  const [destinationPreviews, setDestinationPreviews] = useState<(string | null)[]>([]);
  
  // État de chargement global
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Ajoutez ces états pour gérer les images des destinations
  const [destinationFiles, setDestinationFiles] = useState<(File | null)[]>([null, null, null]);

  // Utilisation des queries/mutations importées
  const { loading, error, data } = useQuery(GET_AGENCY, {
    variables: { name: user.agencyName },
    fetchPolicy: 'network-only'
  });
  
  const [updateAgency, { loading: updateLoading }] = useMutation(UPDATE_AGENCY);
  const [uploadFile, { loading: uploadLoading }] = useMutation(UPLOAD_FILE);
  
  // Effet pour initialiser le formulaire avec les données de l'agence
  useEffect(() => {
    if (data?.agency) {
      const agency = data.agency;
      
      // Initialiser le formulaire avec les données de l'agence
      reset({
        name: agency.name,
        logo: agency.logo,
        description: agency.description,
        address: agency.address,
        phone: agency.phone,
        email: agency.email,
        website: agency.website,
        firstTitle: agency.firstTitle,
        secondTitle: agency.secondTitle,
        imageCover: agency.imageCover,
    socialMedia: {
          facebook: agency.facebook,
          instagram: agency.instagram,
          tiktok: agency.tiktok
        },
        popularDestinations: agency.popularDestinations || [],
        aboutImage: agency.aboutImage,
        yearsExperience: agency.yearsExperience,
        expertise: agency.expertise,
        satisfiedTravelers: agency.satisfiedTravelers,
        prestations: Array.isArray(agency.prestations) ? agency.prestations : []
      });
      
      // Initialiser le thème avec les données de l'agence
      if (agency.theme) {
        setTheme(agency.theme);
      }
      
      // ... autres initialisations ...
    }
  }, [data, reset]);
  
  // Mettre à jour l'état de soumission en fonction des états de chargement
  useEffect(() => {
    setIsSubmitting(updateLoading || uploadLoading);
  }, [updateLoading, uploadLoading]);
  
  // Fonction pour soumettre le formulaire
  const onSubmit = async (formData: FormInputs) => {
    try {
      setIsSubmitting(true);
      
      // Upload du logo si modifié
      let logoUrl = formData.logo;
      if (logoFile) {
        logoUrl = await uploadFileToServer(logoFile, 'logo');
      }

      // Upload de l'image de couverture si modifiée
      let coverUrl = formData.imageCover;
      if (coverFile) {
        coverUrl = await uploadFileToServer(coverFile, 'imageCover');
      }

      // Upload de l'image "à propos" si modifiée
      let aboutUrl = formData.aboutImage;
      if (aboutFile) {
        aboutUrl = await uploadFileToServer(aboutFile, 'aboutImage');
      }
      
      // Préparer les données pour la mise à jour
      // Supprimer __typename du thème
      const { __typename, ...themeWithoutTypename } = theme;
      
      // Nettoyer les destinations populaires (prix >= 0)
      const cleanedDestinations = (formData.popularDestinations || []).map(dest => {
        // Si c'est un objet avec __typename, le supprimer
        if (typeof dest === 'object' && dest !== null) {
          const { __typename, id, ...destWithoutTypename } = dest;
          return {
            ...destWithoutTypename,
            price: Math.max(0, Number(destWithoutTypename.price) || 0)
          };
        }
        return dest;
      });
      
      const input = {
        name: formData.name,
        logo: logoUrl,
        description: formData.description,
        address: formData.address,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        firstTitle: formData.firstTitle,
        secondTitle: formData.secondTitle,
        imageCover: coverUrl,
        facebook: formData.socialMedia.facebook,
        instagram: formData.socialMedia.instagram,
        tiktok: formData.socialMedia.tiktok,
        popularDestinations: cleanedDestinations,
        aboutImage: aboutUrl,
        yearsExperience: parseInt(formData.yearsExperience as unknown as string) || 0,
        expertise: formData.expertise,
        satisfiedTravelers: parseInt(formData.satisfiedTravelers as unknown as string) || 0,
        prestations: (formData.prestations || []).filter(Boolean),
        theme: themeWithoutTypename
      };
      
      // Mettre à jour l'agence
      const { data } = await updateAgency({
        variables: { input }
      });
      
      alert('Profil mis à jour avec succès!');
      
    } catch (error: any) {
      console.error('Erreur détaillée lors de la mise à jour:', error);
      if (error.graphQLErrors) {
        console.error('GraphQL errors:', error.graphQLErrors);
      }
      if (error.networkError) {
        console.error('Network error:', error.networkError);
      }
      alert(`Erreur: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonctions pour gérer la sélection des fichiers
  const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCoverFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAboutSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setAboutFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setAboutPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Fonction pour gérer le changement d'image d'une destination
  const handleDestinationImageChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Créer un objet URL pour la prévisualisation
      const previewUrl = URL.createObjectURL(file);
      
      // Mettre à jour la prévisualisation pour cette destination
      const newPreviews = [...destinationPreviews];
      newPreviews[index] = previewUrl;
      setDestinationPreviews(newPreviews);
      
      // Convertir l'image en base64 pour l'enregistrement
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        
        // Mettre à jour le champ image de la destination
        const destinations = [...watch('popularDestinations')];
        destinations[index] = {
          ...destinations[index],
          image: base64String
        };
        setValue('popularDestinations', destinations);
      };
      reader.readAsDataURL(file);
    }
  };

  // Initialiser les refs pour les inputs de fichiers des destinations
  useEffect(() => {
    destinationInputRefs.current = destinationInputRefs.current.slice(0, watch('popularDestinations')?.length || 0);
    
    // Initialiser les prévisualisations d'images des destinations
    const destinations = watch('popularDestinations') || [];
    setDestinationPreviews(destinations.map(dest => dest.image || null));
  }, [watch('popularDestinations')?.length]);

  // Fonction pour compresser l'image avant l'envoi
  const compressImage = async (file: File, maxSizeMB = 1): Promise<File> => {
    // Importer dynamiquement la bibliothèque browser-image-compression
    const imageCompression = await import('browser-image-compression');
    
    const options = {
      maxSizeMB: maxSizeMB,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    };
    
    try {
      const compressedFile = await imageCompression.default(file, options);
      return new File([compressedFile], file.name, { type: compressedFile.type });
    } catch (error) {
      console.error('Erreur lors de la compression de l\'image:', error);
      return file; // Retourner le fichier original en cas d'erreur
    }
  };

  // Modifier la fonction uploadFileToServer pour utiliser un nom de champ générique pour les destinations
  const uploadFileToServer = async (file: File, fieldName: string): Promise<string> => {
    try {
      
      // Compresser l'image si c'est une image et qu'elle est trop grande
      let fileToUpload = file;
      if (file.type.startsWith('image/') && file.size > 1024 * 1024) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true
        };
        
        try {
          const imageCompression = await import('browser-image-compression');
          fileToUpload = await imageCompression.default(file, options);
        } catch (compressError) {
          console.error('Erreur lors de la compression:', compressError);
          // Continuer avec le fichier original
        }
      }
      
      // Convertir en base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(fileToUpload);
      });
            
      // Utiliser un nom de champ générique pour les destinations
      const actualFieldName = fieldName.startsWith('popularDestination') ? 'destination' : fieldName;
      
      const { data } = await uploadFile({
        variables: { 
          file: base64, 
          agencyName: user.agencyName || '', 
          fieldName: actualFieldName 
        }
      });
      
      return data?.uploadFile?.url || '';
    } catch (error) {
      console.error('Erreur détaillée lors de l\'upload:', error);
      throw error;
    }
  };

  // Ajouter cette fonction
  const checkFileSize = (file: File): boolean => {
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (file.size > maxSize) {
      alert(`Le fichier ${file.name} est trop volumineux (${(file.size / 1024 / 1024).toFixed(2)} MB). La taille maximale est de 5 MB.`);
      return false;
    }
    return true;
  };

  // Rendu des sections du formulaire
  const renderBasicInfo = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
        Informations de base
              </h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'agence <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
            {...register('name')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Nom de votre agence"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo
                  </label>
          <div className="flex items-center space-x-4">
            {(logoPreview || watch('logo')) && (
              <div className="w-24 h-24 border rounded-md overflow-hidden">
                <img
                  src={logoPreview || getImageUrl(watch('logo'))}
                  alt="Logo preview"
                  className="w-full h-full object-cover"
                      />
                    </div>
                  )}
            <div>
              <input
                type="file"
                ref={logoInputRef}
                onChange={handleLogoSelect}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Upload className="h-4 w-4 mr-2" />
                Choisir un logo
              </button>
              <p className="mt-1 text-xs text-gray-500">
                Format recommandé: PNG ou JPG, 1:1 ratio
              </p>
            </div>
          </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
            {...register('description')}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Description de votre agence"
                  />
                </div>
              </div>
            </div>
  );

  const renderHeroSection = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900">
          Section d&apos;accueil
        </h2>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-sm font-medium text-gray-700">Couleur du texte</span>
          <input
            type="color"
            value={theme.heroTextColor ?? '#FFFFFF'}
            onChange={(e) => setTheme((prev) => ({ ...prev, heroTextColor: e.target.value }))}
            className="w-9 h-9 rounded-lg cursor-pointer border border-gray-300"
            title="Couleur du titre et sous-titre"
          />
        </label>
      </div>
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Titre principal
          </label>
          <input
            type="text"
            {...register('firstTitle')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Ex: Bienvenue chez [Nom de l'agence]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sous-titre
          </label>
          <input
            type="text"
            {...register('secondTitle')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Ex: Votre partenaire de voyage de confiance"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Image de couverture
          </label>
          <div className="flex items-center space-x-4">
            {(coverPreview || watch('imageCover')) && (
              <div className="w-32 h-24 border rounded-md overflow-hidden">
                <img
                  src={coverPreview || getImageUrl(watch('imageCover'))}
                  alt="Cover preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div>
              <input
                type="file"
                ref={coverInputRef}
                onChange={handleCoverSelect}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Upload className="h-4 w-4 mr-2" />
                Choisir une image
              </button>
              <p className="mt-1 text-xs text-gray-500">
                Format recommandé: JPG ou PNG, 16:9 ratio
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContactInfo = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
        Informations de contact
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse
                  </label>
                  <input
                    type="text"
            {...register('address')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Adresse complète"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
            type="text"
            {...register('phone')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Ex: +213 123 456 789"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
            {...register('email')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="contact@votreagence.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Site web
                  </label>
                  <input
            type="text"
            {...register('website')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="https://www.votreagence.com"
                  />
                </div>
              </div>
            </div>
  );

  const renderSocialMedia = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Réseaux sociaux
              </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facebook
                  </label>
                  <input
            type="text"
            {...register('socialMedia.facebook')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="https://facebook.com/votreagence"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instagram
                  </label>
                  <input
            type="text"
            {...register('socialMedia.instagram')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="https://instagram.com/votreagence"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    TikTok
                  </label>
                  <input
            type="text"
            {...register('socialMedia.tiktok')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="https://tiktok.com/@votreagence"
          />
        </div>
      </div>
    </div>
  );

  const renderAboutSection = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">
        À propos de nous
      </h2>
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Image "À propos"
          </label>
          <div className="flex items-center space-x-4">
            {(aboutPreview || watch('aboutImage')) && (
              <div className="w-32 h-24 border rounded-md overflow-hidden">
                <img
                  src={aboutPreview || getImageUrl(watch('aboutImage'))}
                  alt="About preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div>
              <input
                type="file"
                ref={aboutInputRef}
                onChange={handleAboutSelect}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => aboutInputRef.current?.click()}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Upload className="h-4 w-4 mr-2" />
                Choisir une image
              </button>
              <p className="mt-1 text-xs text-gray-500">
                Format recommandé: JPG ou PNG, 4:3 ratio
              </p>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Années d'expérience
          </label>
          <input
            type="number"
            {...register('yearsExperience', { valueAsNumber: true })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Ex: 10"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Expertise
          </label>
          <input
            type="text"
            {...register('expertise')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Ex: Voyages sur mesure, Omra, etc."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Voyageurs satisfaits
          </label>
          <input
            type="number"
            {...register('satisfiedTravelers', { valueAsNumber: true })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Ex: 1000"
          />
        </div>
      </div>
    </div>
  );

  const renderPrestationsSection = () => {
    const prestations = watch('prestations') || [];
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Nos prestations
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Liste des prestations ou services proposés par votre agence (affichés sur la page d&apos;accueil).
        </p>
        <div className="space-y-3">
          {prestations.map((_, index) => (
            <div key={index} className="flex gap-2 items-center">
              <input
                type="text"
                {...register(`prestations.${index}`)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Ex: Organisation de l'Omra, Voyages sur mesure..."
              />
              <button
                type="button"
                onClick={() => {
                  const next = prestations.filter((_, i) => i !== index);
                  setValue('prestations', next);
                }}
                className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                title="Supprimer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setValue('prestations', [...prestations, ''])}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
          >
            <Plus className="h-4 w-4" />
            Ajouter une prestation
          </button>
        </div>
      </div>
    );
  };

  const renderPopularDestinations = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">
        Destinations populaires
      </h2>
      <div className="space-y-6">
        {watch('popularDestinations')?.map((destination, index) => (
          <div key={index} className="border p-4 rounded-md">
            <h3 className="font-medium mb-3">Destination {index + 1}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom
                </label>
                <input
                  type="text"
                  {...register(`popularDestinations.${index}.name`)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Ex: Omra"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image
                </label>
                <div className="flex flex-col space-y-2">
                  {destinationPreviews[index] && (
                    <div className="relative w-full h-32 mb-2 border rounded-md overflow-hidden">
                      <img
                        src={destinationPreviews[index] || ''}
                        alt={`Destination ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newPreviews = [...destinationPreviews];
                          newPreviews[index] = null;
                          setDestinationPreviews(newPreviews);
                          
                          const destinations = [...watch('popularDestinations')];
                          destinations[index] = {
                            ...destinations[index],
                            image: ''
                          };
                          setValue('popularDestinations', destinations);
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 w-5 h-5 flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    onChange={(e) => handleDestinationImageChange(e, index)}
                    className="hidden"
                    accept="image/*"
                    ref={(el) => {
                      if (destinationInputRefs.current) {
                        destinationInputRefs.current[index] = el;
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => destinationInputRefs.current?.[index]?.click()}
                    className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Sélectionner une image
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prix
                </label>
                <input
                  type="number"
                  min={0}
                  {...register(`popularDestinations.${index}.price`, {
                    valueAsNumber: true,
                    min: { value: 0, message: 'Le prix ne peut pas être négatif' }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Ex: 250000"
                />
                {errors.popularDestinations?.[index]?.price && (
                  <span className="text-red-500 text-sm mt-1">
                    {errors.popularDestinations[index]?.price?.message}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
        
        <button
          type="button"
          onClick={() => {
            const destinations = [...(watch('popularDestinations') || [])];
            destinations.push({ name: '', image: '', price: 0 });
            setValue('popularDestinations', destinations);
          }}
          className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une destination
        </button>
      </div>
    </div>
  );

  const renderThemeCustomizer = () => {
    const logoUrl = logoPreview || (watch('logo') ? getImageUrl(watch('logo')) : null);
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Personnalisation du thème
        </h2>
        <ThemeCustomizer theme={theme} onChange={setTheme} logoUrl={logoUrl} />
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          Profil de l'agence
        </h1>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader className="animate-spin h-8 w-8 text-blue-600" />
            <span className="ml-2 text-gray-600">Chargement...</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="pb-24">
            {renderBasicInfo()}
            {renderHeroSection()}
            {renderContactInfo()}
            {renderSocialMedia()}
            {renderAboutSection()}
            {renderPrestationsSection()}
            {renderPopularDestinations()}
            {renderThemeCustomizer()}

            <div className="fixed bottom-0 left-0 md:left-64 right-0 z-30 bg-white border-t border-gray-200 shadow-lg py-4 px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl mx-auto flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <Loader className="animate-spin h-4 w-4 mr-2" />
                      Enregistrement...
                    </span>
                  ) : (
                    'Enregistrer les modifications'
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </AdminLayout>
  );
}